import { Component, Input, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { BatchMaterialOptionDto, SlurryBatchDto } from '../../../../services/slurry.service';

type BatchActionMessageScope = 'batch' | 'batchDetails';

interface SlurryBatchesTabStatusBucket {
  label: string;
  count: number;
}

interface SlurryBatchesTabParentFacade {
  blockedBatchActionMessage: string;
  blockedBatchDetailsActionMessage: string;
  batchesLoaded: boolean;
  showBatchDetailsSections: boolean;
  effectiveBatchMeasurementTolerance: number;
  onBlockedActionClick(reason: string, scope: BatchActionMessageScope): void;
  openBatchDetails(batchId: number): void;
  getBatchTestsSummary(batch: SlurryBatchDto): string;
  getBatchEditLockReason(): string;
  batchStatusLabel(status: number | null | undefined): string;
  getSelectedBatchEditableOutputCount(): number;
  canEditSelectedBatch(): boolean;
  shouldShowFoamerFluidLossWarning(): boolean;
  hasCustomBatchMeasurementTolerance(): boolean;
  onBatchToleranceToggle(checked: boolean): void;
  onBatchMixAsFoamToggle(checked: boolean): void;
  canToggleBatchMixAsFoam(): boolean;
  getExpectedFoamWeight(): number | null;
  getFoamerMaterialOptions(): BatchMaterialOptionDto[];
  isFoamerCandidateSelected(slurryMaterialId: number): boolean;
  onFoamerCandidateToggle(slurryMaterialId: number, checked: boolean): void;
  canChangeBatchFoamerSelection(): boolean;
  isMixAsFoamSelectionReadyForSubmit(): boolean;
  getSaveBatchHeaderBlockedReason(): string;
  saveBatchHeader(): void;
  getSubmitSelectedBatchBlockedReason(): string;
  submitSelectedBatch(): void;
  getRedoSelectedBatchBlockedReason(): string;
  redoSelectedBatch(): void;
  getRedoDryMixBlockedReason(): string;
  redoDryMix(): void;
  getRedoLiquidMixBlockedReason(): string;
  redoLiquidMix(): void;
  getRedoFoamMixBlockedReason(): string;
  redoFoamMix(): void;
  getRedoMudMixBlockedReason(): string;
  redoMudMix(): void;
  getPrintBatchBlockedReason(): string;
  printSelectedBatch(): void;
  getSelectedBatchStatusBreakdown(): SlurryBatchesTabStatusBucket[];
  batchTestStatusLabel(status: number | null | undefined): string;
  getBatchMaterialName(slurryMaterialId: number | null | undefined): string;
  onMeasuredMaterialSelectionChange(): void;
  getSelectedMeasuredMaterial(): unknown | null;
  getSelectedMaterialTargetAmount(): number | null;
  isMeasuredMaterialContainerAcceptable(): boolean;
  isMeasuredMaterialWeightAcceptable(): boolean;
  saveMeasuredMaterial(): void;
  canSaveMeasuredMaterial(): boolean;
  getMeasuredMaterialLockReason(): string;
  canEditMeasuredFoamWeight(): boolean;
  canEditMeasuredFoamCup(): boolean;
  isMeasuredFoamWeightAcceptable(): boolean;
  saveMeasuredFoam(): void;
  canSaveMeasuredFoam(): boolean;
  getMeasuredFoamLockReason(): string;
  hasMeasuredMudCapture(): boolean;
  isMeasuredMudFormValid(): boolean;
  saveMeasuredMud(): void;
  canSaveMeasuredMud(): boolean;
  getMeasuredMudLockReason(): string;
}

/**
 * SlurryBatchesTabComponent is a dumb/display-only component.
 * All batch business logic (loading, saving, API calls) resides in the parent slurry-details.component.
 * This component only renders UI and emits user intent events to the parent.
 */
@Component({
  selector: 'app-slurry-batches-tab',
  templateUrl: './slurry-batches-tab.component.html',
  styleUrls: ['./slurry-batches-tab.component.css'],
  imports: [CommonModule, FormsModule, MatTooltipModule, MatDialogModule, MatMenuModule],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class SlurryBatchesTabComponent {
  @Input({ required: true }) parent!: SlurryBatchesTabParentFacade;
  // Parent passes all required data via @Input
  @Input() batches: any[] = [];
  @Input() showCreateBatchSection = false;
  @Input() batchMessage = '';
  @Input() createBatchNotes = '';
  @Input() allTestInputs: Array<{ testInputMasterId: number; displayLabel: string; }> = [];
  @Input() selectedTestMasterIds: number[] = [];
  @Input() createBatchBlockedReason = '';
  @Input() printSelectedBatchTestsBlockedReason = '';
  @Input() selectedBatchId: number | null = null;
  @Input() selectedBatch: any | null = null;
  @Input() batchHeader: any = {};
  @Input() testSettings: any = {};
  @Input() capabilities: any | null = null;
  @Input() isParentProjectCompleted = false;
  @Input() statusId = 0;
  @Input() slurryId = 0;
  @Input() projectType = 0;
  @Input() batchMixAsFoam = false;
  @Input() batchDetailsMessage = '';
  @Input() measuredMaterialMessage = '';
  @Input() measuredFoamMessage = '';
  @Input() measuredMudMessage = '';
  @Input() measuredMaterials: any[] = [];
  @Input() measuredMaterialForm: any = {};
  @Input() measuredFoamForm: any = {};
  @Input() measuredMudForm: any = {};
  @Input() batchMaterialOptions: any[] = [];
  @Input() foamerMaterialOptions: any[] = [];
  @Input() selectedFoamerMaterialIds: number[] = [];
  @Input() batchTests: any[] = [];
  @Input() batchToleranceSpecified = false;
  @Input() defaultBatchMeasurementTolerance = 0;

  // Child emits events to parent for user actions
  @Output() batchSelected = new EventEmitter<number>();
  @Output() createBatchRequested = new EventEmitter<void>();
  @Output() createBatchNotesChange = new EventEmitter<string>();
  @Output() batchTestSelectionChanged = new EventEmitter<{ testInputMasterId: number; checked: boolean; }>();
  @Output() printAllBatchTestsRequested = new EventEmitter<void>();
  @Output() printSelectedBatchTestsRequested = new EventEmitter<void>();
  @Output() batchSaveHeader = new EventEmitter<void>();
  @Output() batchSubmit = new EventEmitter<void>();
  @Output() batchDetailsOpen = new EventEmitter<number>();
  @Output() batchDetailsClose = new EventEmitter<void>();
  @Output() measuredMaterialSave = new EventEmitter<void>();
  @Output() measuredMaterialDelete = new EventEmitter<number>();
  @Output() measuredMaterialSelectionChange = new EventEmitter<void>();
  @Output() measuredFoamSave = new EventEmitter<void>();
  @Output() measuredFoamDelete = new EventEmitter<number>();
  @Output() measuredMudSave = new EventEmitter<void>();
  @Output() measuredMudDelete = new EventEmitter<number>();
  @Output() batchRedo = new EventEmitter<void>();
  @Output() batchPrint = new EventEmitter<void>();

  onBatchRowClick(batchId: number): void {
    this.batchSelected.emit(batchId);
  }

  onBatchRowDoubleClick(batchId: number): void {
    this.batchDetailsOpen.emit(batchId);
  }

  isBatchTestSelected(testInputMasterId: number): boolean {
    return this.selectedTestMasterIds.includes(testInputMasterId);
  }

  onCreateBatchNotesInput(notes: string): void {
    this.createBatchNotesChange.emit(notes);
  }

  onBatchTestSelectionChange(testInputMasterId: number, checked: boolean): void {
    this.batchTestSelectionChanged.emit({ testInputMasterId, checked });
  }

  onCreateBatch(): void {
    this.createBatchRequested.emit();
  }

  onPrintAllBatchTests(): void {
    this.printAllBatchTestsRequested.emit();
  }

  onPrintSelectedBatchTests(): void {
    this.printSelectedBatchTestsRequested.emit();
  }

  onSaveBatchHeader(): void {
    this.batchSaveHeader.emit();
  }

  onSubmitBatch(): void {
    this.batchSubmit.emit();
  }

  onSaveMeasuredMaterial(): void {
    this.measuredMaterialSave.emit();
  }

  onDeleteMeasuredMaterial(id: number): void {
    this.measuredMaterialDelete.emit(id);
  }

  onMeasuredMaterialSelectionChange(): void {
    this.measuredMaterialSelectionChange.emit();
  }

  onSaveMeasuredFoam(): void {
    this.measuredFoamSave.emit();
  }

  onDeleteMeasuredFoam(id: number): void {
    this.measuredFoamDelete.emit(id);
  }

  onSaveMeasuredMud(): void {
    this.measuredMudSave.emit();
  }

  onDeleteMeasuredMud(id: number): void {
    this.measuredMudDelete.emit(id);
  }

  onRedoBatch(): void {
    this.batchRedo.emit();
  }

  onPrintBatch(): void {
    this.batchPrint.emit();
  }
}
